// Generic renderer-side log-forward wrapper. Calls into the Electron
// preload `window.iris.forwardLog` bridge (W1 — posthog flume), which
// the main process validates and emits via `loggy[level]`. The first
// caller is PostHog (W2) but the primitive is intentionally generic:
// any renderer subsystem can call
// `forwardLog({source: 'mySubsystem', level: 'warn', message: '...'})`
// without touching main-process code — the 10-min-second-caller DX
// target. Fire-and-forget (`void` return); no Promise, no awaiting.
// When not running inside Electron (jest unit tests, web preview, SSR),
// the call short-circuits silently — no throw, no console noise.
//
// Per rule: think-in-systems (ai.knowledge/global) — generic primitive,
// composable on top by W2 (PostHog) and W3 (accessibility) without
// per-caller plumbing.
// Per rule: stay-in-scope (ai.knowledge/global) — errors+warnings only
// per engineer scope decision.
// Per rule: reference-rules-in-comments (ai.knowledge/global).

// Spec 4 (D1): the recorder has no app imports of its own, so this adds no cycle.
import { beginRendererOperation } from '#~/boot/renderer-startup.js';

export type ForwardLogArgs = {
  source: string;
  level: 'warn' | 'error';
  message: string;
  context?: Record<string, unknown>;
};

// `window.iris.forwardLog` is injected by Electron's preload script
// (studio/electron/src/preload/index.ts). The shape is `(payload: unknown) => void`.
type IrisBridge = {
  forwardLog?: (payload: unknown) => void;
};

export function forwardLog(args: ForwardLogArgs): void {
  // Short-circuit when not in Electron — keeps web previews and unit
  // tests silent. `typeof window` guard covers SSR/Worker contexts too.
  if (typeof window === 'undefined') return;
  const bridge = (window as unknown as { iris?: IrisBridge }).iris;
  if (!bridge || typeof bridge.forwardLog !== 'function') return;

  // Defensive: a preload bug should not surface as a console error in
  // the renderer. The whole point of a log-forwarding primitive is that
  // calling it must never make things worse than not calling it.
  try {
    bridge.forwardLog(args);
  } catch {
    // Intentionally swallowed — see above.
  }
}

// ── Boot-progress clock (F2, report §11.1) ───────────────────────────────────
// A renderer-local "boot still advancing" timestamp, refreshed on every REAL
// boot-state transition (see auto-link-loading.tsx). The TimeoutWatchdog reads
// its age to decide whether a slow boot is advancing (re-arm, don't reboot) or
// wedged (reboot). This is deliberately NOT a periodic keepalive timer: a timer
// would keep "advancing" true on a renderer whose event loop is alive but whose
// boot is genuinely stuck, suppressing the watchdog forever and losing the only
// renderer-side recovery path. Tying progress to real phase transitions means a
// boot that stops transitioning is observably stalled, so a true wedge still
// recovers — while a boot that keeps advancing through phases is left alone.
// Self-contained (no telemetry IPC) by design.
// Per rule: think-in-systems + reference-rules-in-comments (ai.knowledge/global).
let lastBootProgressAt: number | null = null;

// Record that the boot just advanced a phase. Cheap, synchronous, no IPC.
export function noteBootProgress(now: number = Date.now()): void {
  lastBootProgressAt = now;
}

// Ms since the last boot-progress mark, or null if none has fired yet. null
// reads as "no progress observed" — the watchdog treats it as a failure signal
// (a boot that never advanced is the genuine wedge the watchdog exists to
// recover).
export function msSinceLastBootProgress(now: number = Date.now()): number | null {
  if (lastBootProgressAt === null) return null;
  return now - lastBootProgressAt;
}

// ── Boot-hop timing diagnostics (perf/boot-latency) ─────────────────────────
// Structured per-hop timing for the boot-critical path (auto-link checking
// phase + synchronizer registry load). Each wrapped await emits ONE line:
//   IRIS_DIAG_BOOT_HOP name=<hop> ms=<n> ok=<bool>
// The e2e harness launches Electron with ELECTRON_ENABLE_LOGGING=1
// (e2e/fixtures/electron.fixture.ts), which streams renderer console output
// into the captured process stdout — so these lines are readable straight
// from the CI job log. Cost is one console.log per hop (~15 lines per boot),
// negligible next to the network hops it measures. The line is emitted on
// rejection too (ok=false) so a timed-out hop still shows its wall-clock cost.
// Per rule: think-in-systems + reference-rules-in-comments (ai.knowledge/global).
// Spec 4 (D1): the same hop now ALSO opens a recorder operation span, so one
// instrumented wait appears both in the CI-readable log line and in the
// per-document diagnostics. The log text is unchanged — existing greps and the
// e2e receipts keep working. `lane` defaults to 'boot-hop'; callers that re-run
// (the synchronizer effect) pass a per-run lane so repeats are distinguishable
// instead of collapsing into one bucket.
//
// Its argument is an ALREADY-CREATED promise, so this measures the async wait
// only: synchronous work done while constructing that promise happens before the
// span opens. Labelled as such rather than reported as total cost.
export async function timeBootHop<T>(name: string, op: Promise<T>, lane = 'boot-hop'): Promise<T> {
  const start = performance.now();
  const closeSpan = beginRendererOperation(name, lane);
  const emit = (ok: boolean): void => {
    console.log(
      `IRIS_DIAG_BOOT_HOP name=${name} ms=${Math.round(performance.now() - start)} ok=${ok}`
    );
  };
  try {
    const result = await op;
    emit(true);
    closeSpan('ok');
    return result;
  } catch (error) {
    emit(false);
    closeSpan('error');
    throw error;
  }
}

// Keep the boot-progress clock fresh WHILE a single bounded cloud operation is
// in flight (S8 / F2 completeness, audit GAP1). A fresh-link's linkAgent attempt
// participant-waits ~29s on the cloud with no intermediate state transition; the
// watchdog's 17s window would otherwise go stale mid-attempt and reboot a
// healthy-but-slow link. This ticks progress every `intervalMs` for as long as
// `op` is pending, then STOPS the instant it settles — so suppression is bounded
// by the op's own timeout (a reboot mid-cloud-call would only lose the in-flight
// link and hit the same slow cloud again). Distinct from a mount-lifetime
// keepalive timer (which would suppress the watchdog forever on a wedged
// renderer): this is tied to one bounded promise, not "overlay still mounted".
// Per rule: think-in-systems (ai.knowledge/global).
export async function withBootProgressHeartbeat<T>(op: Promise<T>, intervalMs: number): Promise<T> {
  noteBootProgress();
  const handle = setInterval(() => noteBootProgress(), Math.max(1, intervalMs));
  try {
    return await op;
  } finally {
    clearInterval(handle);
  }
}
